'use client';

import { useMemo, useState, useCallback } from 'react';
import type { ColumnConfig } from '../types';
import { calculateSummary } from '../utils/exportUtils';

export interface GroupedData {
  [key: string]: any[];
}

export const useGrouping = (data: any[], groupedColumns: string[], columns: ColumnConfig[] = []) => {
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  const groupedData = useMemo(() => {
    if (groupedColumns.length === 0) return null;

    const groupData = (items: any[], columns: string[], level = 0): any => {
      if (columns.length === 0) return items;

      const [currentColumn, ...remainingColumns] = columns;
      const grouped: { [key: string]: any[] } = {};

      items.forEach(item => {
        const key = String(item[currentColumn] || 'Unknown');
        if (!grouped[key]) {
          grouped[key] = [];
        }
        grouped[key].push(item);
      });

      if (remainingColumns.length > 0) {
        Object.keys(grouped).forEach(key => {
          grouped[key] = groupData(grouped[key], remainingColumns, level + 1);
        });
      }

      return grouped;
    };

    return groupData(data, groupedColumns);
  }, [data, groupedColumns]);

  const toggleGroupExpansion = useCallback((groupKey: string) => {
    setExpandedGroups(prev => {
      const newExpanded = new Set(prev);
      if (newExpanded.has(groupKey)) {
        newExpanded.delete(groupKey);
      } else {
        newExpanded.add(groupKey);
      }
      return newExpanded;
    });
  }, []);

  const expandAllGroups = useCallback(() => {
    if (!groupedData) return;

    const getAllGroupKeys = (data: any, prefix = ''): string[] => {
      const keys: string[] = [];
      if (typeof data === 'object' && !Array.isArray(data)) {
        Object.keys(data).forEach(key => {
          const groupKey = prefix ? `${prefix}.${key}` : key;
          keys.push(groupKey);
          if (typeof data[key] === 'object' && !Array.isArray(data[key])) {
            keys.push(...getAllGroupKeys(data[key], groupKey));
          }
        });
      }
      return keys;
    };

    const allKeys = getAllGroupKeys(groupedData);
    setExpandedGroups(new Set(allKeys));
  }, [groupedData]);

  const collapseAllGroups = useCallback(() => {
    setExpandedGroups(new Set());
  }, []);

  // Helper function to extract all data items from a group (handles nested groups)
  const getAllDataFromGroup = (groupData: any): any[] => {
    if (Array.isArray(groupData)) {
      return groupData;
    }

    if (typeof groupData === 'object') {
      const allData: any[] = [];
      Object.values(groupData).forEach(value => {
        allData.push(...getAllDataFromGroup(value));
      });
      return allData;
    }

    return [];
  };

  // Calculate group summaries for columns with summaryType
  const calculateGroupSummaries = (groupData: any) => {
    const summaries: { [field: string]: { value: any; label: string; type: string } } = {};
    const allData = getAllDataFromGroup(groupData);

    columns.forEach(column => {
      if (column.summaryType && column.field) {
        const summaryValue = calculateSummary(allData, column.field, column.summaryType);
        let formattedValue: string | number = summaryValue;

        // Format currency values
        if (column.columnType === 'currency') {
          // INR by default — see the note in hooks/useDataGrid.tsx. 'DOLLAR' is opt-in.
          const symbol = column.displayFormat === 'DOLLAR' ? '$' : '₹';
          const locale = column.displayFormat === 'DOLLAR' ? 'en-US' : 'en-IN';
          formattedValue = `${symbol}${summaryValue.toLocaleString(locale)}`;
        } else if (column.columnType === 'number') {
          // Use Indian locale for number formatting
          formattedValue = `₹${summaryValue.toLocaleString('en-IN')}`;
        }

        // Format average values to 2 decimal places
        if (column.summaryType === 'avg' && typeof summaryValue === 'number') {
          const locale = column.displayFormat === 'DOLLAR' ? 'en-US' : 'en-IN';
          formattedValue = column.columnType === 'currency'
            ? `${column.displayFormat === 'DOLLAR' ? '$' : '₹'}${summaryValue.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
            : summaryValue.toFixed(2);
        }

        summaries[column.field] = {
          value: formattedValue,
          label: column.summaryLabel || column.summaryType,
          type: column.summaryType
        };
      }
    });

    return summaries;
  };

  const renderGroupedRows = useCallback((
    data: any,
    columns: any[],
    table: any,
    level = 0,
    prefix = '',
    onRowClick?: (rowData: any) => void
  ): JSX.Element[] => {
    const rows: JSX.Element[] = [];

    if (Array.isArray(data)) {
      // Base case: render actual data rows
      data.forEach((row, index) => {
        const tableRow = table.getRowModel().rows.find((r: any) =>
          JSON.stringify(r.original) === JSON.stringify(row)
        );

        if (tableRow) {
          rows.push(
            <tr
              key={`row-${prefix}-${index}`}
              className="hover:bg-gray-50 cursor-pointer"
              onClick={() => onRowClick?.(row)}
            >
              {tableRow.getVisibleCells().map((cell: any) => (
                <td
                  key={cell.id}
                  className={`px-6 py-3 whitespace-nowrap text-sm text-gray-900 ${level > 0 ? `pl-${Math.min(level * 4 + 6, 20)}` : ''
                    }`}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          );
        }
      });
    } else if (typeof data === 'object') {
      // Recursive case: render group headers and sub-groups
      Object.entries(data).forEach(([key, value]) => {
        const groupKey = prefix ? `${prefix}.${key}` : key;
        const isExpanded = expandedGroups.has(groupKey);
        const itemCount = Array.isArray(value)
          ? value.length
          : Object.values(value).flat().length;

        // Group header row with column-aligned summaries
        const summaries = calculateGroupSummaries(value);
        const visibleColumns = table.getVisibleLeafColumns();
        const totalColSpan = visibleColumns.length;
        const hasSummaries = Object.keys(summaries).length > 0;
        // Use full-width colspan when expanded or no summaries to show
        const useFullWidth = isExpanded || !hasSummaries;
        const indentPx = level * 20 + 16;

        rows.push(
          <tr key={`group-${groupKey}`} className={`${level === 0 ? 'bg-gray-100' : 'bg-gray-50'} border-b border-gray-200`}>
            {useFullWidth ? (
              <td
                key={`group-cell-${groupKey}`}
                colSpan={totalColSpan}
                className="px-6 py-3"
                style={{ paddingLeft: `${indentPx}px` }}
              >
                <div className="flex items-center">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleGroupExpansion(groupKey);
                    }}
                    className="mr-3 p-1 hover:bg-gray-300 rounded"
                  >
                    {isExpanded ? (
                      <i className="ri-arrow-down-s-line w-4 h-4 flex items-center justify-center"></i>
                    ) : (
                      <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center"></i>
                    )}
                  </button>

                  <div className="flex items-center gap-2">
                    <i className="ri-folder-3-line w-4 h-4 flex items-center justify-center text-violet-600"></i>
                    <span className="font-semibold text-gray-800 whitespace-nowrap">
                      {key}
                    </span>
                    <span className="text-xs text-gray-500 whitespace-nowrap">
                      (<span className="font-semibold">{itemCount}</span> record{itemCount !== 1 ? 's' : ''})
                    </span>
                  </div>
                </div>
              </td>
            ) : (
              visibleColumns.map((column: any, columnIndex: number) => {
                const columnField = column.columnDef.accessorKey || column.columnDef.id;
                const colSummary = summaries[columnField];

                // First column contains the group info and expand/collapse button
                if (columnIndex === 0) {
                  return (
                    <td
                      key={column.id}
                      className="px-6 py-3"
                      style={{ paddingLeft: `${indentPx}px` }}
                    >
                      <div className="flex items-center">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleGroupExpansion(groupKey);
                          }}
                          className="mr-3 p-1 hover:bg-gray-300 rounded"
                        >
                          {isExpanded ? (
                            <i className="ri-arrow-down-s-line w-4 h-4 flex items-center justify-center"></i>
                          ) : (
                            <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center"></i>
                          )}
                        </button>

                        <div className="flex items-center gap-2">
                          <i className="ri-folder-3-line w-4 h-4 flex items-center justify-center text-violet-600"></i>
                          <span className="font-semibold text-gray-800 whitespace-nowrap">
                            {key}
                          </span>
                          <span className="text-xs text-gray-500 whitespace-nowrap">
                            (<span className="font-semibold">{itemCount}</span> record{itemCount !== 1 ? 's' : ''})
                          </span>
                        </div>

                        {colSummary && (
                          <div className="ml-4 text-right">
                            <div className="text-sm font-semibold text-gray-900">
                              {colSummary.value}
                            </div>
                            <div className="text-xs text-gray-500 capitalize">
                              {colSummary.label}
                            </div>
                          </div>
                        )}
                      </div>
                    </td>
                  );
                }

                // Other columns show summaries when collapsed
                return (
                  <td
                    key={column.id}
                    className="px-6 py-3 text-center"
                  >
                    {colSummary && (
                      <div className="text-right">
                        <div className="text-sm font-semibold text-gray-900">
                          {colSummary.value}
                        </div>
                        <div className="text-xs text-gray-500 capitalize">
                          {colSummary.label}
                        </div>
                      </div>
                    )}
                  </td>
                );
              })
            )}
          </tr>
        );

        // Render sub-groups or data rows if expanded
        if (isExpanded) {
          rows.push(...renderGroupedRows(value, columns, table, level + 1, groupKey, onRowClick));

          // Add summary row at the bottom when expanded (only for leaf groups with actual data)
          if (Array.isArray(value)) {
            const summaryEntries = Object.entries(summaries);
            if (summaryEntries.length > 0) {
              rows.push(
                <tr key={`group-summary-${groupKey}`} className={`${level === 0 ? 'bg-violet-50' : 'bg-violet-25'} border-t-2 border-violet-200 font-semibold`}>
                  {visibleColumns.map((column: any, columnIndex: number) => {
                    const columnField = column.columnDef.accessorKey || column.columnDef.id;
                    const hasSummary = summaries[columnField];

                    // First column shows "Total" or "Summary" label
                    if (columnIndex === 0) {
                      return (
                        <td
                          key={`summary-${column.id}`}
                          className={`px-6 py-3 ${level > 0 ? `pl-${Math.min(level * 4 + 6, 20)}` : ''}`}
                        >
                          <div className="flex items-center">
                            <i className="ri-calculator-line mr-2 w-4 h-4 flex items-center justify-center text-violet-600"></i>
                            <span className="font-semibold text-violet-800">
                              Group Total
                            </span>
                            {/* Show summary in first column if it has one */}
                            {hasSummary && (
                              <div className="ml-4 text-right">
                                <div className="text-sm font-semibold text-violet-900">
                                  {hasSummary.value}
                                </div>
                                <div className="text-xs text-violet-600 capitalize">
                                  {hasSummary.label}
                                </div>
                              </div>
                            )}
                          </div>
                        </td>
                      );
                    }

                    // Other columns show their summaries
                    return (
                      <td
                        key={`summary-${column.id}`}
                        className="px-6 py-3 text-center"
                      >
                        {hasSummary && (
                          <div className="text-right">
                            <div className="text-sm font-semibold text-violet-900">
                              {hasSummary.value}
                            </div>
                            <div className="text-xs text-violet-600 capitalize">
                              {hasSummary.label}
                            </div>
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            }
          }
        }
      });
    }

    return rows;
  }, [expandedGroups, toggleGroupExpansion, columns]);

  return {
    groupedData,
    expandedGroups,
    toggleGroupExpansion,
    expandAllGroups,
    collapseAllGroups,
    renderGroupedRows,
  };
};

import { flexRender } from '@tanstack/react-table';